import { useEffect, useRef, useState } from 'react';

/**
 * Появление блока при входе во вьюпорт.
 *
 * Зачем анимация: последовательное раскрытие секций задаёт порядок чтения.
 * Реализовано на IntersectionObserver, слушатель `scroll` не используется:
 * он срабатывает на каждый кадр прокрутки и вызывает лишние перерисовки.
 * При `prefers-reduced-motion: reduce` контент показывается сразу.
 */
export function useReveal<T extends HTMLElement>(options: { amount?: number } = {}) {
  const { amount = 0.15 } = options;
  const ref = useRef<T>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.disconnect();
          }
        }
      },
      { threshold: amount, rootMargin: '0px 0px -8% 0px' },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [amount]);

  return { ref, revealed };
}
